import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField, useAssignees } from "@/lib/crm";
import { PageHeader, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { ActivityList } from "@/components/activities/ActivityList";
import { ACT_TYPES, ActivityDialog, PRIORITIES } from "@/components/activities/ActivityDialog";

const BUCKETS = [["overdue", "Scadute"], ["today", "Oggi"], ["upcoming", "Prossime"], ["nodate", "Senza data"], ["completed", "Completate"]];

export default function ActivitiesPage() {
  const { me, can } = useAuth();
  const users = useAssignees();
  const all = ["all", "team"].includes(me?.role?.data_scope);
  const [scope, setScope] = useState("me");
  const [person, setPerson] = useState("");
  const [bucket, setBucket] = useState("today");
  const [type, setType] = useState("");
  const [priority, setPriority] = useState("");
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  const [sp, setSp] = useSearchParams();
  const [linked, setLinked] = useState(null);
  const linkId = sp.get("id");
  useEffect(() => {
    if (!linkId) { return; }
    api.get(`/activities/${linkId}`).then((r) => setLinked(r.data)).catch((e) => { toast.error(errorMessage(e)); setSp({}, { replace: true }); });
  }, [linkId]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeLinked = () => { setLinked(null); setSp({}, { replace: true }); };
  const who = scope !== "me" && person ? { assignee_id: person } : {};
  useEffect(() => { api.get("/activities/summary", { params: { scope, ...who } }).then((r) => setCounts(r.data)).catch(() => {}); }, [scope, key, person]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const f = () => setKey((k) => k + 1); window.addEventListener("focus", f); return () => window.removeEventListener("focus", f); }, []);
  const params = { scope, bucket, sort: "due_at", ...who, ...(type ? { type } : {}), ...(priority ? { priority } : {}) };

  return (
    <div data-testid="activities-page">
      <PageHeader overline="Operations" title="Attività" description="Cosa fare, per chi, entro quando."
        actions={can("activities:create") && <Button className="gap-2" onClick={() => setOpen(true)} data-testid="activities-new-button"><Plus />Nuova attività</Button>} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="inline-flex rounded-control border border-line bg-white p-0.5" data-testid="activities-scope">
          {[["me", "Le mie"], ...(all ? [["team", "Team"], ["all", "Tutte"]] : [])].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)} className={`rounded-[8px] px-3 py-1.5 text-sm ${scope === k ? "bg-brand-50 font-semibold text-ink" : "text-slate-500"}`} data-testid={`activities-scope-${k}`}>{l}</button>))}
        </div>
        {scope !== "me" && <SelectField value={person} onChange={setPerson} options={users} allowNone="Tutte le persone" className="sm:w-52" testId="activities-filter-person" />}
        <SelectField value={type} onChange={setType} options={ACT_TYPES} allowNone="Tutti i tipi" className="sm:w-44" testId="activities-filter-type" />
        <SelectField value={priority} onChange={setPriority} options={PRIORITIES} allowNone="Tutte le priorità" className="sm:w-44" testId="activities-filter-priority" />
      </div>
      <div className="-mx-4 mb-3 flex gap-1 overflow-x-auto px-4 sm:mx-0 sm:px-0" data-testid="activities-buckets">
        {BUCKETS.map(([k, l]) => (
          <button key={k} onClick={() => setBucket(k)} className={`shrink-0 rounded-full border px-3 py-1.5 text-sm ${bucket === k ? "border-brand-200 bg-brand-50 font-semibold text-ink" : "border-line text-slate-600"}`} data-testid={`activities-bucket-${k}`}>
            {l} <span className={k === "overdue" && counts.overdue ? "font-semibold text-red-600" : "text-slate-400"}>{counts[k] ?? ""}</span></button>))}
      </div>
      <Panel className="px-4 py-1 sm:px-5"><ActivityList params={params} reloadKey={key} empty="Niente qui. Ottimo lavoro." /></Panel>
      <ActivityDialog open={open} onOpenChange={setOpen} onSaved={() => setKey((k) => k + 1)} />
      <ActivityDialog open={!!linked} onOpenChange={(v) => !v && closeLinked()} activity={linked} onSaved={() => setKey((k) => k + 1)} />
    </div>
  );
}
